import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Gift, Menu, LogOut, Heart, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const links = [
  { to: "/", label: "Home" },
  { to: "/quiz", label: "Find My Gift" },
  { to: "/browse", label: "Browse Ideas" },
];

export default function Navbar() {
  const { user, logout, setAuthOpen } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-brand-cream/85 border-b border-brand-cream2/70">
      <div className="max-w-7xl mx-auto px-4 lg:px-10 py-3.5 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <span className="w-9 h-9 rounded-full bg-brand-green flex items-center justify-center transition-transform group-hover:rotate-12">
            <Gift className="w-5 h-5 text-brand-cream" />
          </span>
          <span className="font-serif text-xl font-semibold tracking-tight text-brand-ink">Choicefully</span>
          <span className="hidden sm:inline text-[11px] font-mono px-1.5 py-0.5 rounded bg-brand-cream2 text-muted-foreground">🇬🇧 £ GBP</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}-link`}
              className="px-3.5 py-2 text-sm font-medium text-brand-ink/80 hover:text-brand-green rounded-full hover:bg-brand-cream2 transition-colors">
              {l.label}
            </Link>
          ))}
          {user && (
            <Link to="/saved" data-testid="nav-saved-link"
              className="px-3.5 py-2 text-sm font-medium text-brand-ink/80 hover:text-brand-green rounded-full hover:bg-brand-cream2 transition-colors">
              Saved
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <Button onClick={() => navigate("/quiz")} data-testid="nav-start-quiz-button"
            className="hidden sm:inline-flex bg-brand-green hover:bg-brand-greendark rounded-full px-5">
            Start Quiz
          </Button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-avatar-button" className="rounded-full outline-none ring-offset-2 focus:ring-2 ring-brand-green">
                  <Avatar className="w-9 h-9 border border-brand-cream2">
                    <AvatarImage src={user.picture} />
                    <AvatarFallback className="bg-brand-burgundy text-brand-cream text-sm">
                      {(user.name || "U").slice(0, 1).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white w-52">
                <div className="px-2 py-1.5 text-sm">
                  <p className="font-medium truncate">{user.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/saved")} data-testid="menu-saved" className="cursor-pointer">
                  <Heart className="w-4 h-4 mr-2" /> Saved & History
                </DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin" className="cursor-pointer">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="cursor-pointer text-brand-burgundy">
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="ghost" onClick={() => setAuthOpen(true)} data-testid="nav-signin-button"
              className="hover:bg-brand-cream2 rounded-full">
              Sign in
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
